import mixpanel from "mixpanel-browser";
import { useState, useCallback } from "react";
import { endPoints, postData } from "../../../utilities/networkCalls";

interface UseNpsReturn {
    npsStatus: boolean;
    isLoading: boolean;
    isSubmitting: boolean;
    isDismissing: boolean;
    checkNpsStatus: () => Promise<void>;
    saveRating: (rating: number) => Promise<{ success: boolean; requiresFeedback: boolean }>;
    saveFeedback: (feedback: string) => Promise<{ success: boolean }>;
    completeRatingFive: () => Promise<{ success: boolean }>;
    dismiss: () => Promise<{ success: boolean }>;
    openShopifyReviewModal: () => Promise<void>;
}

export const useNps = (): UseNpsReturn => {
    const [npsStatus, setNpsStatus] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDismissing, setIsDismissing] = useState(false);

    // Check if NPS should be shown
    const checkNpsStatus = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await postData({
                url: endPoints.npsStatus,
                payload: {},
            });

            if (response?.data?.showNps) {
                setNpsStatus(response.data.showNps);
            }
        } catch (error) {
            console.error("Error checking NPS status:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Save rating (1-5)
    const saveRating = useCallback(async (rating: number) => {
        setIsSubmitting(true);
        try {
            const response = await postData({
                url: endPoints.npsSaveRating,
                payload: { rating },
            });

            if (response?.data?.success) {
                return {
                    success: true,
                    requiresFeedback: response.data.requiresFeedback,
                };
            }
            return { success: false, requiresFeedback: false };
        } catch (error) {
            console.error("Error saving rating:", error);
            return { success: false, requiresFeedback: false };
        } finally {
            setIsSubmitting(false);
        }
    }, []);

    // Save feedback for ratings 1-4
    const saveFeedback = useCallback(async (feedback: string) => {
        setIsSubmitting(true);
        try {
            const response = await postData({
                url: endPoints.npsSaveFeedback,
                payload: { feedback },
            });

            if (response?.data?.success) {
                setNpsStatus(false); // Hide banner
                return { success: true };
            }
            return { success: false };
        } catch (error) {
            console.error("Error saving feedback:", error);
            return { success: false };
        } finally {
            setIsSubmitting(false);
        }
    }, []);

    // Complete rating 5 (after Shopify modal closes)
    const completeRatingFive = useCallback(async () => {
        try {
            const response = await postData({
                url: endPoints.npsCompleteRatingFive,
                payload: {},
            });

            if (response?.data?.success) {
                setNpsStatus(false); // Hide banner
                return { success: true };
            }
            return { success: false };
        } catch (error) {
            console.error("Error completing rating 5:", error);
            return { success: false };
        }
    }, []);

    // Dismiss NPS banner
    const dismiss = useCallback(async () => {
        setIsDismissing(true);
        try {
            const response = await postData({
                url: endPoints.npsDismiss,
                payload: {},
            });

            if (response?.data?.success) {
                setNpsStatus(false); // Hide banner
                return { success: true };
            }
            return { success: false };
        } catch (error) {
            console.error("Error dismissing NPS:", error);
            return { success: false };
        } finally {
            setIsDismissing(false);
        }
    }, []);

    // Shopify Review Modal
    const openShopifyReviewModal = useCallback(async () => {
        try {
            // @ts-expect-error reviews are not available in older packages
            const result = await shopify.reviews.request();
            if (!result.success) {
                mixpanel.track("nps_feedback", { success: result.success, code: result.code, message: result.message });
            }
            await completeRatingFive();
        } catch (error) {
            console.error("Error opening review modal:", error);
        }
    }, [completeRatingFive]);

    return {
        npsStatus,
        isLoading,
        isSubmitting,
        isDismissing,
        checkNpsStatus,
        saveRating,
        saveFeedback,
        completeRatingFive,
        dismiss,
        openShopifyReviewModal,
    };
};
